/**
 * BIL Store Locator Google Maps wrapper.
 * Uses the standard Google Maps marker API so the locator is not dependent
 * on Cloud map styling or a Map ID.
 */
(function (window) {
  'use strict';

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps) return Promise.resolve(window.google);
    if (!apiKey) return Promise.reject(new Error('Missing Google Maps browser API key.'));

    if (window.__BIL_GOOGLE_MAPS_PROMISE__) return window.__BIL_GOOGLE_MAPS_PROMISE__;

    window.__BIL_GOOGLE_MAPS_PROMISE__ = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) + '&v=weekly';
      script.async = true;
      script.defer = true;
      script.onload = function () {
        if (window.google && window.google.maps) resolve(window.google);
        else reject(new Error('Google Maps loaded without the Maps API.'));
      };
      script.onerror = function () {
        reject(new Error('Google Maps could not be loaded. Check your browser key and restrictions.'));
      };
      document.head.appendChild(script);
    });

    return window.__BIL_GOOGLE_MAPS_PROMISE__;
  }

  function createMap(element, config) {
    var map = new google.maps.Map(element, {
      center: { lat: Number(config.defaultLat), lng: Number(config.defaultLng) },
      zoom: Number(config.defaultZoom) || 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      gestureHandling: 'cooperative'
    });

    return Promise.resolve({
      map: map,
      InfoWindow: google.maps.InfoWindow,
      LatLngBounds: google.maps.LatLngBounds,
      Marker: google.maps.Marker
    });
  }

  function escapeHtml(value) {
    return (value || '').toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function storeAddress(store) {
    return [
      store.address_1,
      store.address_2,
      [store.city, store.state, store.zip].filter(Boolean).join(' ')
    ].filter(Boolean).join(', ');
  }

  function directionsUrlForStore(store) {
    return 'https://www.google.com/maps/dir/?api=1&destination=' +
      encodeURIComponent(storeAddress(store));
  }

  function markerIcon(accentColor) {
    var color = (accentColor || '#FF5A1F').replace('#', '%23');
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">' +
        '<path fill="#121212" d="M20 1C9.5 1 1 9.5 1 20c0 14.25 19 31 19 31s19-16.75 19-31C39 9.5 30.5 1 20 1z"/>' +
        '<path fill="' + color + '" d="M20 4C10.9 4 3.5 11.4 3.5 20c0 11.65 16.5 27.2 16.5 27.2S36.5 31.65 36.5 20C36.5 11.4 29.1 4 20 4z"/>' +
        '<circle cx="20" cy="20" r="11" fill="#fff"/>' +
        '<text x="20" y="25" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="14" fill="#121212">B</text>' +
        '</svg>'
      ),
      scaledSize: new google.maps.Size(32, 42),
      anchor: new google.maps.Point(16, 42)
    };
  }

  function renderMarkers(context, stores, options) {
    options = options || {};
    var onMarkerClick = options.onMarkerClick || function () {};
    return stores.filter(function (store) {
      return typeof store.latitude === 'number' && typeof store.longitude === 'number';
    }).map(function (store) {
      var marker = new context.Marker({
        map: context.map,
        position: { lat: store.latitude, lng: store.longitude },
        title: store.store_name + ', ' + storeAddress(store),
        icon: markerIcon(options.accentColor)
      });
      marker.addListener('click', function () {
        onMarkerClick(store, marker);
      });
      return { store: store, marker: marker };
    });
  }

  function clearMarkers(entries) {
    (entries || []).forEach(function (entry) {
      entry.marker.setMap(null);
    });
  }

  function infoContent(store) {
    var phone = store.phone ? '<p class="bil-map-popup__phone">' + escapeHtml(store.phone) + '</p>' : '';
    return '' +
      '<div class="bil-map-popup">' +
      '<p class="bil-map-popup__eyebrow">' + escapeHtml(store.retailer) + '</p>' +
      '<h3>' + escapeHtml(store.store_name) + '</h3>' +
      '<p>' + escapeHtml(storeAddress(store)) + '</p>' +
      phone +
      '<a href="' + directionsUrlForStore(store) + '" target="_blank" rel="noopener">Get directions</a>' +
      '</div>';
  }

  function openInfoWindow(context, marker, store, infoWindow) {
    infoWindow.setContent(infoContent(store));
    infoWindow.open(context.map, marker);
  }

  function focusStore(context, marker, store, infoWindow) {
    context.map.panTo({ lat: store.latitude, lng: store.longitude });
    context.map.setZoom(Math.max(context.map.getZoom() || 0, 14));
    openInfoWindow(context, marker, store, infoWindow);
  }

  function fitToMarkers(context, entries, fallback) {
    if (!entries || !entries.length) {
      if (fallback) {
        context.map.setCenter(fallback);
        context.map.setZoom(4);
      }
      return;
    }
    if (entries.length === 1) {
      context.map.setCenter(entries[0].marker.getPosition());
      context.map.setZoom(13);
      return;
    }
    var bounds = new context.LatLngBounds();
    entries.forEach(function (entry) {
      bounds.extend(entry.marker.getPosition());
    });
    context.map.fitBounds(bounds, 48);
  }

  function geocodeUS(term) {
    return new Promise(function (resolve, reject) {
      if (!window.google || !google.maps || !term) {
        reject(new Error('Geocoding is not available.'));
        return;
      }
      var geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: term + ', USA', region: 'US' }, function (results, status) {
        if (status === 'OK' && results && results[0]) {
          var location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng(), label: results[0].formatted_address });
        } else {
          reject(new Error('Could not locate that search.'));
        }
      });
    });
  }

  window.BILStoreLocatorMap = {
    loadGoogleMaps: loadGoogleMaps,
    createMap: createMap,
    renderMarkers: renderMarkers,
    clearMarkers: clearMarkers,
    openInfoWindow: openInfoWindow,
    focusStore: focusStore,
    fitToMarkers: fitToMarkers,
    directionsUrlForStore: directionsUrlForStore,
    geocodeUS: geocodeUS,
    storeAddress: storeAddress
  };
})(window);
